import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const shipping = subtotal >= 50 || subtotal === 0 ? 0 : 6.99;
  const total = subtotal + shipping;

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-[#F8F9FA] flex items-center justify-center mx-auto mb-6">
          <ShoppingBag className="w-9 h-9 text-gray-400" />
        </div>
        <h1 className="font-display font-black text-3xl mb-3">Your cart is empty</h1>
        <p className="text-gray-500 mb-8">Find the right solution for your cat's needs.</p>
        <Link to="/shop" data-testid="cart-empty-shop" className="inline-flex items-center gap-2 rounded-full bg-[#FF5C28] text-white font-bold px-8 py-4 hover:bg-[#E04D1E] transition-colors">
          Browse Shop <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-display font-black text-4xl tracking-tight mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((i) => (
            <div key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex gap-4 bg-white border border-gray-100 rounded-2xl p-4">
              <div className="w-24 h-24 bg-[#F8F9FA] rounded-xl flex-shrink-0">
                <img src={i.image} alt={i.name} className="w-full h-full object-contain p-2" />
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${i.slug}`} className="font-display font-bold hover:text-[#FF5C28]">{i.name}</Link>
                <p className="text-gray-500 text-sm">${i.price.toFixed(2)} each</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center bg-[#F8F9FA] rounded-full border border-gray-200">
                    <button onClick={() => updateQty(i.slug, i.quantity - 1)} data-testid={`cart-minus-${i.slug}`} className="p-2 hover:text-[#FF5C28]"><Minus className="w-4 h-4" /></button>
                    <span className="w-8 text-center font-bold text-sm">{i.quantity}</span>
                    <button onClick={() => updateQty(i.slug, i.quantity + 1)} data-testid={`cart-plus-${i.slug}`} className="p-2 hover:text-[#FF5C28]"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => removeItem(i.slug)} data-testid={`cart-remove-${i.slug}`} className="text-gray-400 hover:text-red-500"><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="font-display font-bold text-lg">${(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="bg-[#F8F9FA] rounded-3xl p-6 h-fit">
          <h2 className="font-display font-bold text-xl mb-5">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-bold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span className="font-bold">{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
            {subtotal < 50 && subtotal > 0 && <p className="text-xs text-[#FF5C28] font-semibold">Add ${(50 - subtotal).toFixed(2)} more for free shipping!</p>}
            <div className="border-t border-gray-200 pt-3 flex justify-between font-display font-black text-lg">
              <span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={checkout} disabled={loading} data-testid="checkout-btn"
            className="w-full mt-6 rounded-full bg-[#FF5C28] text-white font-bold px-8 py-4 hover:bg-[#E04D1E] transition-colors disabled:opacity-60">
            {loading ? "Redirecting…" : "Checkout securely"}
          </button>
          <p className="text-xs text-gray-400 text-center mt-3">Secure payment via Stripe</p>
        </div>
      </div>
    </div>
  );
}
